import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { SharedModule } from '@shared/shared.module';
import { PermissionChange } from '@app/features/dashboard/group-register/permission-tree/permission-tree.models';

export interface PermissionSummaryView {
  permissions: number;
  modules: string[];
  menuScreens: number;
}

/** Até três nomes por extenso; daí em diante, "e mais N". */
export function joinModuleNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  if (names.length <= 3) return `${names.slice(0, -1).join(', ')} e ${names[names.length - 1]}`;
  return `${names.slice(0, 2).join(', ')} e mais ${names.length - 2} módulos`;
}

/**
 * Faixa de resumo do grupo ("X permissões em Y módulos") e, ao abrir, o que muda ao salvar em frases curtas
 * (ganha/perde), com "Desfazer tudo".
 */
@Component({
  selector: 'app-permission-summary',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './permission-summary.component.html',
  styleUrls: ['./permission-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PermissionSummaryComponent {
  readonly summary = input.required<PermissionSummaryView>();
  readonly changes = input<PermissionChange[]>([]);
  /** Claims antigas do grupo que o catálogo do papel não traz; são mantidas ao salvar. */
  readonly keptUnknown = input(0);
  readonly isEditing = input(false);

  readonly undoAll = output<void>();
  /** Desfaz uma linha só (valor da claim). */
  readonly undoOne = output<string>();

  readonly open = signal(false);

  readonly modulesText = computed(() => joinModuleNames(this.summary().modules));

  readonly added = computed(() => this.changes().filter(change => change.type === 'added'));
  readonly removed = computed(() => this.changes().filter(change => change.type === 'removed'));

  /** Lista longa (ex.: "Tudo" num módulo) mostra as primeiras e diz quantas faltam. */
  readonly maxListed = 10;

  readonly changesLabel = computed(() => {
    const total = this.changes().length;
    return total === 1 ? '1 alteração não salva' : `${total} alterações não salvas`;
  });

  toggle(): void {
    this.open.update(value => !value);
  }

  undo(): void {
    this.undoAll.emit();
    this.open.set(false);
  }
}
